import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HabboImage from './HabboImage';
import EditableBox from './profile/EditableBox';
import BioBox from './profile/BioBox';
import ReportProfileModal from './profile/ReportProfileModal';
import { getPresetBackgrounds } from '../data/profileSkins';
import {
    getProfilePageLoad,
    getProfileData,
    getProfileRelationship,
    sendProfileFriendRequest,
    removeProfileFriend,
    blockProfileUser,
    unblockProfileUser,
    getFranklyCloudItemsForUser,
} from '../services/api';
import { decodeCloudItem } from '../utils/cloudItemUtils';
import { fetchProfileColor, resolveCosmeticUrl } from '../services/profileColorService';
import {
    useEmoteContext,
    renderEmotesInHtmlMemo,
    EmotePickerModal,
} from '../services/emoteService';
import PostItem from './profile/PostItem';
import {
    buildOnMyMindPalette,
    timeAgo,
    blockConfirmBody,
    decodeEntities,
    externalizeAndMentionHtml,
    ExternalLinkModal,
} from './profile/PostThread';


// ── Helpers (identical to Profile.jsx) ───────────────────────────────────────
const sanitiseHex = (v) => { const s = String(v ?? '').trim(); return /^#[0-9A-Fa-f]{6}$/.test(s) ? s : ''; };
const decodeEntitiesToEmojis = (s) => String(s ?? '').replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)));
const _hexToRgb = (hex) => { const h = String(hex||'').trim(); if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return {r:255,g:255,b:255}; return {r:parseInt(h.slice(1,3),16),g:parseInt(h.slice(3,5),16),b:parseInt(h.slice(5,7),16)}; };
const _srgbToLinear = (v) => { const x=v/255; return x<=0.04045?x/12.92:Math.pow((x+0.055)/1.055,2.4); };
const _luminance = (hex) => { const {r,g,b}=_hexToRgb(hex); return 0.2126*_srgbToLinear(r)+0.7152*_srgbToLinear(g)+0.0722*_srgbToLinear(b); };

// ── MOODS — copied from Profile.jsx ──────────────────────────────────────────
const MOODS = [
    { key: 'happy',     label: 'Happy',     icon: '•' },
    { key: 'excited',   label: 'Excited',   icon: '¥' },
    { key: 'loved',     label: 'Loved',     icon: 'ƒ' },
    { key: 'proud',     label: 'Proud',     icon: 'º' },
    { key: 'relaxed',   label: 'Relaxed',   icon: 'µ' },
    { key: 'silly',     label: 'Silly',     icon: '¿' },
    { key: 'sleepy',    label: 'Sleepy',    icon: 'µ' },
    { key: 'hungry',    label: 'Hungry',    icon: '¬' },
    { key: 'bored',     label: 'Bored',     icon: '÷' },
    { key: 'confused',  label: 'Confused',  icon: '¡' },
    { key: 'anxious',   label: 'Anxious',   icon: '†' },
    { key: 'sad',       label: 'Sad',       icon: '÷' },
    { key: 'angry',     label: 'Angry',     icon: '‡' },
    { key: 'sick',      label: 'Sick',      icon: 'ª' },
    { key: 'motivated', label: 'Motivated', icon: '¶' },
    { key: 'creative',  label: 'Creative',  icon: 'Õ' },
    { key: 'gaming',    label: 'Gaming',    icon: '»' },
    { key: 'working',   label: 'Working',   icon: '±' },
    { key: 'vibing',    label: 'Vibing',    icon: '—' },
    { key: 'nostalgic', label: 'Nostalgic', icon: '|' },
];

// ── ProfileMoodBubble — read-only copy from Profile.jsx ──────────────────────
const ProfileMoodBubble = ({ mood }) => {
    const moodObj = MOODS.find(m => m.key === mood);
    if (!moodObj) return null;
    return (
        <div className="relative flex-shrink-0">
            <div style={{ fontSize: '9px' }} className="relative z-[1] inline-block h-[32px] select-none cursor-default">
                <div className="relative h-[32px] pl-[22px] pr-[12px]">
                    <div className="absolute inset-y-0 left-0" style={{ right: '12px', backgroundImage: 'url(/img/moodbub.png)', backgroundRepeat: 'no-repeat', backgroundPosition: 'left top', backgroundSize: '102px 32px', imageRendering: 'pixelated' }} />
                    <div className="absolute top-0 right-0 w-[12px] h-[32px]" style={{ backgroundImage: 'url(/img/moodbub.png)', backgroundRepeat: 'no-repeat', backgroundPosition: 'right top', backgroundSize: '102px 32px', imageRendering: 'pixelated' }} />
                    <span className="relative -top-[2px] z-[1] h-full flex items-center gap-1 whitespace-nowrap font-volterBold leading-none pointer-events-none text-coal/80">
                        <span>{moodObj.label}</span>
                        <span>{moodObj.icon}</span>
                    </span>
                </div>
            </div>
        </div>
    );
};

// ── ConfirmDialog — copied from Profile.jsx ───────────────────────────────────
const ConfirmDialog = ({ state, onClose }) => {
    if (!state) return null;
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3">
            <div className="w-full max-w-sm border border-black/40 bg-offwhite rounded shadow-md overflow-hidden">
                <div className="px-3 py-2 border-b border-black/20 font-bold text-coal">{state.title}</div>
                <div className="p-3 text-sm text-coal">{state.message}</div>
                <div className="p-2 border-t border-black/20 flex justify-end gap-2 bg-black/5">
                    <button type="button" onClick={onClose} className="px-3 py-1 border border-black/40 bg-offwhite text-coal text-sm rounded hover:bg-black/5">No</button>
                    <button type="button" onClick={async () => { onClose(); if (typeof state.onConfirm === 'function') await state.onConfirm(); }} className="px-3 py-1 border border-black/40 bg-gold text-coal text-sm rounded hover:brightness-95">Yes</button>
                </div>
            </div>
        </div>
    );
};

// ── PostThreadModal ───────────────────────────────────────────────────────────
const PostThreadModal = ({
    post, profileUsername, userData, isLoggedIn, isGuest,
    restrictCommenting = false, restrictLiking = false, restrictionExpiry = null,
    onClose,
}) => {
    const navigate = useNavigate();
    const emoteCtx = useEmoteContext() ?? { packs: [], emotesByPack: {} };

    const currentUserHabboName = userData?.habboName || userData?.HabboName || userData?.username || '';
    const isOwnProfile = String(profileUsername).toLowerCase() === String(currentUserHabboName).toLowerCase();

    // ── Profile data (mirrors Profile.jsx state shape) ────────────────────────
    const [customisation, setCustomisation] = useState(null);
    const [bannerUrl, setBannerUrl]             = useState('');
    const [profileMood, setProfileMood]         = useState('');
    const [profileBgImageUrl, setProfileBgImageUrl] = useState(null);
    const [pinnedBadgeID, setPinnedBadgeID]         = useState(null);
    const [pinnedBadgeUploaded, setPinnedBadgeUploaded] = useState(false);
    const [profileAchievements, setProfileAchievements] = useState([]);

    // ── Relationship ──────────────────────────────────────────────────────────
    const [relLoading, setRelLoading]                   = useState(Boolean(isLoggedIn && !isOwnProfile));
    const [isFriend, setIsFriend]                       = useState(false);
    const [isBlocking, setIsBlocking]                   = useState(false);
    const [isBlockedBy, setIsBlockedBy]                 = useState(false);
    const [hasPending, setHasPending]                   = useState(false);
    const [friendActionLoading, setFriendActionLoading] = useState(false);
    const [blockActionLoading, setBlockActionLoading]   = useState(false);

    // ── Cloud items ───────────────────────────────────────────────────────────
    const [cloudItemsMap, setCloudItemsMap]         = useState({});
    const [cloudItemsMapReady, setCloudItemsMapReady] = useState(false);

    useEffect(() => {
        if (!profileUsername) return;
        const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
        const resolveUpload = (src) => (!src || src.startsWith('data:') || src.startsWith('http') ? src : `${apiOrigin}${src.startsWith('/') ? '' : '/'}${src}`);
        getFranklyCloudItemsForUser(profileUsername)
            .then(async data => {
                if (data?.success && Array.isArray(data.items)) {
                    const map = {};
                    await Promise.all(data.items.map(async item => {
                        const decoded = item.type === 'upload' ? { ...item, src: resolveUpload(item.src) } : await decodeCloudItem(item);
                        map[decoded.id] = decoded;
                    }));
                    setCloudItemsMap(map);
                }
            })
            .catch(() => {})
            .finally(() => setCloudItemsMapReady(true));
    }, [profileUsername]);

    // ── Dialogs ───────────────────────────────────────────────────────────────
    const [externalUrl, setExternalUrl]   = useState(null);
    const [confirmState, setConfirmState] = useState(null);
    const requestConfirm = useCallback((opts) => setConfirmState(opts), []);

    // ── Report modal ──────────────────────────────────────────────────────────
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const openReportModal = () => {
        if (!isLoggedIn) { toast.error('You must be logged in to report profiles.'); return; }
        setReportModalOpen(true);
    };

    // ── Emote picker ──────────────────────────────────────────────────────────
    const [isEmotePickerOpen, setIsEmotePickerOpen] = useState(false);
    const emoteInsertRef = React.useRef(null);
    const openEmotePicker = useCallback((insertFn) => { emoteInsertRef.current = insertFn; setIsEmotePickerOpen(true); }, []);
    const handleEmoteSelected = useCallback((token) => { if (typeof emoteInsertRef.current === 'function') emoteInsertRef.current(String(token || '')); setIsEmotePickerOpen(false); }, []);

    // ── Bio panel height measurement (drives post box scroll height on desktop) ─
    const bioPanelRef = useRef(null);
    const [bioHeight, setBioHeight] = useState(null);
    useEffect(() => {
        const el = bioPanelRef.current;
        if (!el) return;
        const obs = new ResizeObserver(([entry]) => setBioHeight(entry.contentRect.height));
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // ── Load profile data ─────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getProfilePageLoad(profileUsername);
                if (cancelled) return;
                const raw = res?.customisation ?? res?.data?.customisation;
                const parsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null;
                if (parsed) setCustomisation(parsed);
                setProfileMood(res?.mood?.mood ?? res?.data?.mood?.mood ?? '');
                if (res?.banner?.url) setBannerUrl(res.banner.url);

                // ── Profile background image ──────────────────────────────────
                if (res?.profile_bg?.url) {
                    setProfileBgImageUrl(res.profile_bg.url);
                } else {
                    let resolvedBg = null;
                    try {
                        const bgR = await getProfileData(profileUsername, 'profile_bg');
                        if (!cancelled && bgR?.success) {
                            let bgP = bgR?.data?.jsonPayload;
                            try { if (typeof bgP === 'string') bgP = JSON.parse(bgP); } catch {}
                            if (bgP?.url) resolvedBg = bgP.url;
                        }
                    } catch {}
                    if (!cancelled) {
                        if (resolvedBg) {
                            setProfileBgImageUrl(resolvedBg);
                        } else if (parsed?.presetBgId) {
                            const preset = getPresetBackgrounds().find(p => p.id === parsed.presetBgId);
                            if (preset) setProfileBgImageUrl(preset.image);
                        }
                    }
                }

                // ── Populate Profile.jsx's sessionStorage cache so navigating to the
                //    profile page skips its Phase-1 fetch (same keys / TTL format).
                const cacheKey = profileUsername.toLowerCase();
                const wrap = (data) => JSON.stringify({ data, ts: Date.now() });
                const achList = Array.isArray(res?.achievements) ? res.achievements : null;
                if (achList) setProfileAchievements(achList);
                try {
                    if (parsed) sessionStorage.setItem(`customisation_${cacheKey}`, wrap(parsed));
                    if (res?.banner?.url) sessionStorage.setItem(`banner_${cacheKey}`, wrap(res.banner));
                    if (achList) sessionStorage.setItem(`achievements_${cacheKey}`, wrap(achList));
                } catch {}

                // Fallback: if no avatarBg in customisation, fetch profile colour service
                if (!parsed?.avatarBg) {
                    fetchProfileColor(profileUsername).then(c => {
                        if (!cancelled) setCustomisation(prev => prev ? { ...prev, avatarBg: prev.avatarBg ?? c } : { avatarBg: c });
                    });
                }
            } catch {
                fetchProfileColor(profileUsername).then(c => {
                    if (!cancelled) setCustomisation(prev => prev ? { ...prev, avatarBg: prev.avatarBg ?? c } : { avatarBg: c });
                });
            }
        })();
        return () => { cancelled = true; };
    }, [profileUsername]);

    // ── Load pinned badge ─────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        getProfileData(profileUsername, 'pinnedBadge')
            .then(pbR => {
                if (cancelled || !pbR?.success) return;
                let payload = pbR?.data?.jsonPayload;
                try { if (typeof payload === 'string') payload = JSON.parse(payload); } catch {}
                setPinnedBadgeID(payload?.badgeID ?? null);
                setPinnedBadgeUploaded(payload?.uploaded ?? false);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [profileUsername]);

    // ── Load relationship ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!isLoggedIn || isOwnProfile) { setRelLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await getProfileRelationship(profileUsername);
                if (cancelled) return;
                const rel = res?.relationship ?? res?.data ?? res;
                setIsFriend(Boolean(rel?.isFriend || rel?.status === 'friends'));
                setIsBlocking(Boolean(rel?.isBlocking));
                setIsBlockedBy(Boolean(rel?.isBlockedBy));
                setHasPending(Boolean(rel?.isPendingOutgoing || rel?.hasPendingRequest || rel?.status === 'pending'));
            } catch {}
            finally { if (!cancelled) setRelLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [profileUsername, isLoggedIn, isOwnProfile]);

    // ── Relationship actions — identical to Profile.jsx ───────────────────────
    const isPendingOutgoing = hasPending && !isFriend;
    const friendButtonLabel = isFriend ? 'Unfriend' : isPendingOutgoing ? 'Pending' : 'Add Friend';
    const blockButtonLabel  = isBlocking ? 'Unblock' : 'Block';
    const canShowActions  = isLoggedIn && !isGuest;
    const actionsDisabled = isOwnProfile;

    const onFriendClick = async () => {
        if (friendActionLoading || isPendingOutgoing) return;
        setFriendActionLoading(true);
        try {
            if (isFriend) {
                const r = await removeProfileFriend(profileUsername);
                if (r?.success !== true) toast.error('Failed to remove friend.');
                else { setIsFriend(false); setHasPending(false); }
            } else {
                const r = await sendProfileFriendRequest(profileUsername);
                if (r?.success !== true) toast.error('Failed to send friend request.');
                else { setHasPending(true); toast.success('Friend request sent!'); }
            }
        }
        catch { toast.error('Friend action failed. Please try again.'); }
        finally { setFriendActionLoading(false); }
    };

    const onBlockClick = () => {
        if (blockActionLoading) return;
        if (isBlocking) {
            requestConfirm({
                title: `Unblock ${profileUsername}?`,
                message: `Are you sure you want to unblock ${profileUsername}?`,
                onConfirm: async () => {
                    setBlockActionLoading(true);
                    try { await unblockProfileUser(profileUsername); setIsBlocking(false); toast.success(`${profileUsername} unblocked.`); }
                    catch { toast.error('Failed to unblock user.'); }
                    finally { setBlockActionLoading(false); }
                },
            });
        } else {
            requestConfirm({
                title: `Block ${profileUsername}?`,
                message: blockConfirmBody(profileUsername),
                onConfirm: async () => {
                    setBlockActionLoading(true);
                    try { await blockProfileUser(profileUsername); setIsBlocking(true); setIsFriend(false); toast.success(`${profileUsername} blocked.`); onClose(); }
                    catch { toast.error('Failed to block user.'); }
                    finally { setBlockActionLoading(false); }
                },
            });
        }
    };

    // ── Scroll lock + ESC ─────────────────────────────────────────────────────
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);

    // ── Derived — same pattern as Profile.jsx ─────────────────────────────────
    const getHeaderColour = useCallback((boxKey) => sanitiseHex(customisation?.headerColours?.[boxKey]) || '#5A2929', [customisation]);
    const getBoxSkin      = useCallback((boxKey) => customisation?.boxSkins?.[boxKey] || 'default', [customisation]);
    const getHeaderTitle  = useCallback((boxKey) => decodeEntitiesToEmojis(customisation?.headerTitles?.[boxKey] || ''), [customisation]);

    const palette   = useMemo(() => buildOnMyMindPalette(getHeaderColour('onMyMind')), [customisation]);
    const blocked   = !relLoading && (isBlocking || isBlockedBy);

    const effectivePinnedBadgeID       = pinnedBadgeID ?? (profileAchievements[0]?.badgeID ?? null);
    const effectivePinnedBadgeUploaded = pinnedBadgeID ? pinnedBadgeUploaded : (profileAchievements[0]?.uploaded ?? false);
    const pinnedAchievement = useMemo(
        () => effectivePinnedBadgeID ? profileAchievements.find(a => a.badgeID === effectivePinnedBadgeID) ?? null : null,
        [effectivePinnedBadgeID, profileAchievements]
    );
    const pageBg    = sanitiseHex(customisation?.pageBg || customisation?.profileBg) || '#f0ece4';
    const mobileUsernameColor = _luminance(pageBg) < 0.16 ? '#f5f5f0' : '#222222';

    // Bio HTML — same pipeline as Profile.jsx
    const bio       = decodeEntitiesToEmojis(customisation?.bio || '');
    const bioHtml   = useMemo(() => renderEmotesInHtmlMemo(externalizeAndMentionHtml(decodeEntities(bio)), emoteCtx), [bio, emoteCtx]);
    const bioHtmlObj = { __html: bioHtml };

    const goProfile = () => { onClose(); navigate(`/members/profile/${encodeURIComponent(profileUsername)}`); };

    // ── Action buttons — identical to Profile.jsx ─────────────────────────────
    const bioActionButtons = canShowActions && (
        <div className="absolute right-0 top-[56px] flex flex-wrap items-end gap-2">
            {!isBlocking && (
                <button type="button" onClick={onFriendClick} disabled={actionsDisabled || relLoading || friendActionLoading || isPendingOutgoing} className="px-3 py-1 border border-black/50 bg-offwhite text-coal text-sm rounded flex items-center gap-2 transition-colors duration-150 hover:bg-gold hover:text-coal active:bg-gold/90 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-offwhite">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/><circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2"/><path d="M20 8v6M23 11h-6" stroke="currentColor" strokeWidth="2"/></svg>
                    {friendButtonLabel}
                </button>
            )}
            <button type="button" onClick={onBlockClick} disabled={actionsDisabled || relLoading || blockActionLoading} className="px-3 py-1 border border-black/50 bg-offwhite text-coal text-sm rounded flex items-center gap-2 transition-colors duration-150 hover:bg-red-600 hover:text-white active:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-offwhite disabled:hover:text-coal">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M7.5 7.5l9 9" stroke="currentColor" strokeWidth="2"/></svg>
                {blockButtonLabel}
            </button>
        </div>
    );

    // ── Avatar background style — identical to Profile.jsx ────────────────────
    const avatarBgInlineStyle = /^\/img\/|^\/circlebgassets\/|^https?:\/\//.test(String(customisation?.avatarBg ?? ''))
        ? { backgroundImage: `url(${resolveCosmeticUrl(customisation.avatarBg)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { backgroundColor: sanitiseHex(customisation?.avatarBg) || 'rgba(255, 255, 255, 1)' };

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 z-[800] bg-black/70 flex flex-col p-4 md:p-0 md:items-center md:justify-center" onClick={onClose}>
                {/* Modal shell */}
                <div
                    className="relative w-full flex-1 md:flex-none md:h-auto md:max-w-6xl md:mx-4 flex flex-col overflow-hidden"
                    style={{ backgroundColor: pageBg, backgroundImage: profileBgImageUrl ? `url(${profileBgImageUrl})` : undefined, backgroundRepeat: profileBgImageUrl ? 'repeat' : undefined, backgroundSize: profileBgImageUrl ? 'auto' : undefined, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close */}
                    <button type="button" onClick={onClose} className="absolute top-2 right-2 z-30 w-8 h-8 flex items-center justify-center bg-black/30 hover:bg-black/50 text-white transition-colors" aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>

                    {/* --- Mobile ---*/}
                    <div className="md:hidden shrink-0">
                        <div className="relative h-[160px] w-full bg-coal_dark overflow-hidden border-b border-black/50">
                            {bannerUrl && <img src={bannerUrl} alt="Banner" className="w-full h-full object-cover" style={{ imageRendering: 'pixelated' }} />}
                            <div className="absolute top-[122px] left-[90px] z-[100]">
                                <ProfileMoodBubble mood={profileMood} />
                            </div>
                        </div>
                        <div className="px-3 pb-1" style={{ backgroundColor: pageBg }}>
                            <div className="flex items-end justify-between -ml-[5px] -mt-[45px] relative">
                                <div className="relative w-[92px] h-[92px] cursor-pointer" onClick={goProfile}>
                                    <div className="w-[92px] h-[92px] rounded-full border border-black/50 flex items-center justify-center overflow-hidden transition-colors duration-200" style={avatarBgInlineStyle}>
                                        <HabboImage habbo={profileUsername} hotel="COM" size="b" direction={2} head_direction={2} gesture="sml" className="relative mt-14" style={{ imageRendering: 'pixelated', scale: 2 }} />
                                    </div>
                                </div>
                                {bioActionButtons}
                            </div>
                            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                                <span className="font-['Jersey_10'] text-3xl leading-none cursor-pointer hover:underline" style={{ color: mobileUsernameColor }} onClick={goProfile}>{profileUsername}</span>
                                {customisation?.pronouns ? (
                                    <span className="text-base font-sans" style={{ color: mobileUsernameColor, opacity: 0.6 }}>[{customisation.pronouns}]</span>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* ── Body ─────────────────────────────────────────────────── */}
                    <div className="flex flex-col md:flex-row flex-1 min-h-0 md:flex-none pt-0 px-3 pb-3 md:p-11 gap-0 md:gap-7">

                        {/* ── Bio box ── */}
                        <div ref={bioPanelRef} className="hidden md:flex md:flex-col shrink-0 w-[360px]">
                            <BioBox
                                profileUsername={profileUsername}
                                bannerUrl={bannerUrl}
                                effectiveIsEditing={false}
                                effectiveIsOwnProfile={false}
                                customisation={customisation}
                                sanitiseHex={sanitiseHex}
                                canShowActions={canShowActions}
                                isBlockingTarget={isBlocking}
                                actionsDisabled={actionsDisabled}
                                relationshipLoading={relLoading}
                                friendActionLoading={friendActionLoading}
                                isPendingOutgoing={isPendingOutgoing}
                                onFriendClick={onFriendClick}
                                friendButtonLabel={friendButtonLabel}
                                onBlockClick={onBlockClick}
                                blockActionLoading={blockActionLoading}
                                blockButtonLabel={blockButtonLabel}
                                profileMood={profileMood}
                                bioHtmlObj={bioHtmlObj}
                                emoteCtx={emoteCtx}
                                getHeaderColour={getHeaderColour}
                                getBoxSkin={getBoxSkin}
                                getHeaderTitle={getHeaderTitle}
                                hasSTFSkin={Boolean(userData?.hasStaffRole)}
                                pinnedBadgeID={effectivePinnedBadgeID}
                                pinnedBadgeUploaded={effectivePinnedBadgeUploaded}
                                pinnedBadgeAchievement={pinnedAchievement}
                                timeAgo={timeAgo}
                                wrapperClassName="flex-1 flex flex-col !mb-0"
                                editableBoxClassName="flex-1 flex flex-col"
                                editableBoxContentClassName="flex-1 flex flex-col"
                                stretchContent={true}
                                guestMode={isGuest}
                                isLoggedIn={isLoggedIn}
                                openReportModal={openReportModal}
                                primaryAction={<button type="button" onClick={goProfile} className="px-3 py-1 border border-black/50 bg-offwhite text-coal text-sm rounded transition-colors duration-150 hover:bg-gold hover:text-coal active:bg-gold/90">View full profile</button>}
                            />
                        </div>

                        {/* onMyMind box */}
                        <div className="flex flex-col flex-1 min-w-0 min-h-0">
                            {blocked ? (
                                <div className="flex items-center justify-center h-full min-h-[200px] p-8 text-center text-sm text-coal/50">
                                    {isBlockedBy ? 'This content is not available.' : 'You have blocked this user.'}
                                </div>
                            ) : (
                                <EditableBox
                                    boxKey="onMyMind"
                                    theme="default"
                                    header={`${profileUsername}'s post`}
                                    padding="p-0"
                                    wrapperClassName="flex flex-col flex-1 min-h-0 !mb-0"
                                    className="flex flex-col flex-1 min-h-0 mb-0"
                                    contentClassName="flex flex-col flex-1 min-h-0"
                                    isEditing={false}
                                    isOwnProfile={isOwnProfile}
                                    isStaff={Boolean(userData?.hasStaffRole)}
                                    getHeaderColour={getHeaderColour}
                                    getBoxSkin={getBoxSkin}
                                >
                                    <div className="overflow-y-scroll habbo-scroll flex-1 min-h-0 md:flex-none md:h-[440px]">
                                        <PostItem
                                            post={post}
                                            profileUsername={profileUsername}
                                            isOwnProfile={isOwnProfile}
                                            userData={isLoggedIn && !isGuest ? userData : null}
                                            timeAgo={timeAgo}
                                            onDeletePost={onClose}
                                            onBlockUser={async (name) => { await blockProfileUser(name); onClose(); }}
                                            likedPostIds={post.isLikedByViewer ? new Set([String(post.id || post.ID)]) : new Set()}
                                            likedCommentIds={new Set()}
                                            likeCountsByPostId={{}}
                                            likeCountsByCommentId={{}}
                                            requestConfirm={requestConfirm}
                                            palette={palette}
                                            checkCommentRateLimit={null}
                                            recordNewComment={null}
                                            mentionFriends={[]}
                                            onExternalLink={setExternalUrl}
                                            onOpenEmotePicker={openEmotePicker}
                                            restrictPosting={false}
                                            restrictCommenting={restrictCommenting}
                                            restrictLiking={restrictLiking}
                                            restrictionExpiry={restrictionExpiry}
                                            isGuest={isGuest}
                                            previewMode={false}
                                            isNarrowColumn={false}
                                            cloudItemsMap={cloudItemsMap}
                                            cloudItemsMapReady={cloudItemsMapReady}
                                        />
                                    </div>
                                </EditableBox>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialogs */}
            {externalUrl && (
                <ExternalLinkModal
                    url={externalUrl}
                    onClose={() => setExternalUrl(null)}
                    onContinue={() => { window.open(externalUrl, '_blank', 'noopener,noreferrer'); setExternalUrl(null); }}
                />
            )}
            <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
            <EmotePickerModal open={isEmotePickerOpen} onClose={() => setIsEmotePickerOpen(false)} onSelect={handleEmoteSelected} emoteCtx={emoteCtx} />
            {reportModalOpen && (
                <ReportProfileModal
                    profileUsername={profileUsername}
                    onClose={() => setReportModalOpen(false)}
                />
            )}
        </>
    );
};

export default PostThreadModal;
